import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { getUuid } from 'store/auth/selectors';
import { selectTeamChatOnlineUsers, selectTeamChatTypingUsers } from 'store/teamChat/selectors';

import { entityTokensToPlainText } from '../TeamChat.entityReferences.utils';
import {
  getChannelDisplayName,
  getChannelId,
  getChannelLastMessage,
  getChannelMembers,
  getChannelRowTypingLabel,
  getChannelUnreadCount,
  getMessageText,
} from '../TeamChat.utils';
import TeamChatChannelTitle from '../TeamChatChannelTitle';
import TeamChatPresenceIndicator from '../TeamChatPresenceIndicator';

import {
  activeRootStyles,
  lastMessagePreviewStyles,
  lastMessageUnreadStyles,
  rootStyles,
  textGroupStyles,
  titleRowStyles,
  typingPreviewStyles,
  unreadBadgeStyles,
} from './TeamChatChannelListItem.styles';

const TeamChatChannelListItem = memo(
  ({ channel, isActive = false, onSelect, actionSlot = null }) => {
    const chatId = getChannelId(channel);
    const lastMessageText = entityTokensToPlainText(getMessageText(getChannelLastMessage(channel)));
    const unreadCount = getChannelUnreadCount(channel);

    const onlineUsers = useSelector(selectTeamChatOnlineUsers(chatId));
    const typingUsers = useSelector(selectTeamChatTypingUsers(chatId));
    const currentUserId = useSelector(getUuid);

    const hasOnlineUsers = !!onlineUsers.length;
    const hasUnread = !!unreadCount;
    const typingLabel = getChannelRowTypingLabel({
      typingUsers,
      members: getChannelMembers(channel),
      currentUserId,
    });

    const onClick = useCallback(() => {
      onSelect(chatId);
    }, [onSelect, chatId]);

    return (
      <Box sx={isActive ? activeRootStyles : rootStyles} onClick={onClick}>
        <Box sx={textGroupStyles}>
          <Box sx={titleRowStyles}>
            <TeamChatPresenceIndicator isOnline={hasOnlineUsers} />
            <TeamChatChannelTitle
              channel={channel}
              fallbackName={getChannelDisplayName(channel)}
              isUnread={hasUnread}
            />
          </Box>
          {typingLabel ? (
            <Typography sx={typingPreviewStyles} variant="regular12">
              {typingLabel}
            </Typography>
          ) : (
            !!lastMessageText && (
              <Typography
                sx={hasUnread ? lastMessageUnreadStyles : lastMessagePreviewStyles}
                variant={hasUnread ? 'medium12' : 'regular12'}
              >
                {lastMessageText}
              </Typography>
            )
          )}
        </Box>
        {hasUnread && (
          <Box sx={unreadBadgeStyles}>
            <Typography variant="medium12">{unreadCount}</Typography>
          </Box>
        )}
        {actionSlot}
      </Box>
    );
  },
);
TeamChatChannelListItem.propTypes = {
  channel: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
  actionSlot: PropTypes.node,
};

export default TeamChatChannelListItem;
